import React, { useState } from "react";
import { useDataRequest } from "../../common/hooks/api";
import { SortRuleDto } from "../../common/service/server/dto/SortRuleDto";
import { PaginatedNewConsentRecordDto } from "../../common/service/server/dto/ConsentRecordDto";
import { NetworkRequestState } from "../../common/models";
import { Api } from "../../common/service/Api";
import { usePrimaryOrganizationId } from "../hooks";
import { usePrivacyCenter } from "./usePrivacyCenter";

interface ConsentStatsDto {
  accepted: number;
  declined: number;
  mixed: number;
}

type UseConsentRecordListing = {
  requests: {
    consentRecords: NetworkRequestState;
  };
  props: ConsentRecordListingProps;
};

export type ConsentRecordListingProps = {
  consentRecords: PaginatedNewConsentRecordDto;
  setPageParam: React.Dispatch<React.SetStateAction<number>>;
  setSortParam: React.Dispatch<React.SetStateAction<SortRuleDto[]>>;
  pageParam: number;
  sortParam: SortRuleDto[];
  loading: boolean;
  refresh: () => void;
};

export const useConsentRecordStats = (
  orgId: string,
  privacyCenterPublicId: string,
): [ConsentStatsDto, NetworkRequestState, () => void] => {
  const { refresh, request, result } = useDataRequest({
    queryKey: ["consentRecordStats", orgId, privacyCenterPublicId],
    api: () => Api.privacyCenter.getConsentRecordsStats(orgId, privacyCenterPublicId),
    messages: {
      defaultError: "Failed to load consent record statistics",
    },
    notReady: !orgId || !privacyCenterPublicId,
  });

  const defaultResult: ConsentStatsDto = {
    accepted: 0,
    declined: 0,
    mixed: 0,
  };

  return [result || defaultResult, request, refresh];
};

export const usePaginatedConsentRecords = (
  orgId: string,
  privacyCenterPublicId: string,
  sortRules: SortRuleDto[],
  pageZeroIndexed: number,
): [PaginatedNewConsentRecordDto, NetworkRequestState, () => void] => {
  const sortParam = sortRules.map((rule) => `${rule.id}:${rule.desc ? "desc" : "asc"}`).join(",");

  const { refresh, request, result } = useDataRequest({
    queryKey: ["consentRecords", orgId, privacyCenterPublicId, sortParam, pageZeroIndexed],
    api: () =>
      Api.privacyCenter.getConsentRecords(orgId, privacyCenterPublicId, {
        page: pageZeroIndexed,
        pageSize: 10,
        ...(sortParam && { sort: sortParam }),
      }),
    messages: {
      defaultError: "Failed to load consent records",
    },
    notReady: !orgId || !privacyCenterPublicId,
  });

  const defaultResult: PaginatedNewConsentRecordDto = {
    data: [],
    meta: { page: pageZeroIndexed + 1, per: 10, count: 0, totalPages: 0, totalCount: 0 },
  };

  return [result || defaultResult, request, refresh];
};

// Main hook that combines pagination state management with data fetching
export const useConsentRecordListing = (): UseConsentRecordListing => {
  const orgId = usePrimaryOrganizationId();
  const [privacyCenter] = usePrivacyCenter(orgId);

  const [pageParam, setPageParam] = useState(1); // 1-indexed for UI
  const [sortParam, setSortParam] = useState<SortRuleDto[]>([]);

  const [consentRecords, consentRecordsRequest, refreshConsentRecords] = usePaginatedConsentRecords(
    orgId,
    privacyCenter?.id,
    sortParam,
    pageParam - 1, // Convert to 0-indexed for API
  );

  return {
    requests: {
      consentRecords: consentRecordsRequest,
    },
    props: {
      consentRecords,
      pageParam,
      sortParam,
      setPageParam,
      setSortParam,
      loading: consentRecordsRequest.running,
      refresh: refreshConsentRecords,
    },
  };
};
